import { useNotifications } from '@armhr/ui';
import { PersonRemoveOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useState } from 'react';

import { useApi } from '../../hooks/useApi';

interface EditAssignmentRow {
  id: number;
  employee_id: string;
  first_name: string;
  last_name: string;
  status: EmployeeHandbookAssignment['status'];
  assigned_at: string | null;
  due_date: string | null;
  handbook_id: number;
}

interface EditAssignmentModalProps {
  open: boolean;
  assignment: EditAssignmentRow;
  onClose: () => void;
  onSaved: () => void;
  onRequestUnassign: () => void;
}

const statusLabels: Record<EmployeeHandbookAssignment['status'], string> = {
  pending: 'Pending',
  signed: 'Signed',
  overdue: 'Overdue',
  read_only: 'View Only',
};

const statusColors: Record<
  EmployeeHandbookAssignment['status'],
  'warning' | 'success' | 'error' | 'default'
> = {
  pending: 'warning',
  signed: 'success',
  overdue: 'error',
  read_only: 'default',
};

const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
  open,
  assignment,
  onClose,
  onSaved,
  onRequestUnassign,
}) => {
  const api = useApi();
  const { showNotification } = useNotifications();

  const initialDate = assignment.due_date
    ? new Date(assignment.due_date)
    : null;

  const [dueDate, setDueDate] = useState<Date | null>(initialDate);
  const [saving, setSaving] = useState(false);

  const hasChanges = dueDate?.toDateString() !== initialDate?.toDateString();

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.company.updateAssignmentDeadline(assignment.id, {
        due_date: dueDate ? dueDate.toISOString() : null,
      });
      showNotification({
        message: `Deadline updated for ${assignment.first_name} ${assignment.last_name}.`,
        severity: 'success',
        autoHideDuration: 4000,
      });
      onSaved();
    } catch {
      showNotification({
        message: 'Unable to update deadline. Please try again.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Assignment</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Employee info */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Employee
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {assignment.first_name} {assignment.last_name}
            </Typography>
          </Box>

          <Stack direction="row" spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={statusLabels[assignment.status]}
                color={statusColors[assignment.status]}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
            {assignment.assigned_at && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {new Date(assignment.assigned_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Deadline editor */}
          {assignment.status !== 'read_only' && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                Deadline
              </Typography>
              <DatePicker
                label="Due date"
                value={dueDate}
                onChange={(val: Date | null) => setDueDate(val)}
                disabled={assignment.status === 'signed'}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          color="error"
          variant="text"
          startIcon={<PersonRemoveOutlined />}
          onClick={onRequestUnassign}
          sx={{ mr: 'auto' }}
        >
          Unassign
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!hasChanges || assignment.status === 'signed'}
          onClick={handleSave}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export type { EditAssignmentRow };
export default EditAssignmentModal;
